import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Box from '@mui/material/Box';

const syntaxHighlight = (json) => {
    if (!json) return '';
    const escaped = json
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

    const tokenized = escaped.replace(
        /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
        (match) => {
            let cls = 'json-number';
            if (/^"/.test(match)) {
                if (/:$/.test(match)) {
                    cls = 'json-key';
                } else {
                    cls = 'json-string';
                }
            } else if (/true|false/.test(match)) {
                cls = 'json-boolean';
            } else if (/null/.test(match)) {
                cls = 'json-null';
            }
            return `<span class="${cls}">${match}</span>`;
        }
    );

    return tokenized.replace(
        /([{}[\],])/g,
        '<span class="json-punctuation">$1</span>'
    );
};

const formatJson = (raw) => {
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        return JSON.stringify(parsed, null, 2);
    } catch {
        return raw;
    }
};

const formatDate = (valeur) => {
    const date = valeur ? new Date(valeur) : null;
    return date && !isNaN(date.getTime()) && date.getFullYear() > 1
        ? date.toLocaleString('fr-FR')
        : null;
};

const styles = `
    .json-viewer .json-key { color: #6c1f8f; }
    .json-viewer .json-string { color: #0f5f2a; }
    .json-viewer .json-number { color: #0550ae; }
    .json-viewer .json-boolean { color: #cf222e; }
    .json-viewer .json-null { color: #6e7781; font-style: italic; }
    .json-viewer .json-punctuation { color: #000000; font-weight: 700; }
`;

const JsonViewerDialog = ({ open, onClose, title, jsonEntries }) => {
    const [copiedIdx, setCopiedIdx] = useState(null);

    const formattedEntries = useMemo(() => {
        if (!jsonEntries) return [];
        return jsonEntries
            .filter(e => e.value)
            .map(e => ({
                label: e.label,
                formatted: formatJson(e.value),
                dateIntegration: formatDate(e.dateIntegration),
                dateModification: formatDate(e.dateModification),
            }));
    }, [jsonEntries]);

    const handleCopy = (text, idx) => {
        navigator.clipboard.writeText(text)
            .then(() => {
                setCopiedIdx(idx);
                setTimeout(() => setCopiedIdx(null), 2000);
            })
            .catch(err => {
                console.error('Erreur lors de la copie :', err);
            });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <style>{styles}</style>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {title || 'Messages JSON'}
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {formattedEntries.length === 0 && (
                    <Box sx={{ color: 'text.secondary', fontStyle: 'italic', p: 2 }}>
                        Aucun message JSON disponible pour cette pièce.
                    </Box>
                )}
                {formattedEntries.map((entry, idx) => (
                    <Box key={idx} sx={{ mb: idx < formattedEntries.length - 1 ? 3 : 0 }}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 1,
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
                                <Box sx={{
                                    fontWeight: 'bold',
                                    fontSize: '0.95rem',
                                    color: 'primary.main'
                                }}>
                                    {entry.label}
                                </Box>
                                {entry.dateIntegration && (
                                    <Box sx={{
                                        fontSize: '0.8rem',
                                        color: 'text.secondary',
                                    }}>
                                        Intégré le {entry.dateIntegration}
                                    </Box>
                                )}
                                {entry.dateModification && (
                                    <Box sx={{
                                        fontSize: '0.8rem',
                                        color: 'text.secondary',
                                    }}>
                                        Modifié le {entry.dateModification}
                                    </Box>
                                )}
                            </Box>
                            <Tooltip title={copiedIdx === idx ? 'Copié !' : 'Copier le JSON formaté'}>
                                <IconButton
                                    size="small"
                                    onClick={() => handleCopy(entry.formatted, idx)}
                                >
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Box
                            className="json-viewer"
                            component="pre"
                            sx={{
                                backgroundColor: '#fdf6e3',
                                border: '1px solid #d0d7de',
                                borderRadius: 1,
                                p: 2,
                                overflow: 'auto',
                                maxHeight: 400,
                                fontSize: '0.82rem',
                                fontFamily: '"Cascadia Code", "Fira Code", "Consolas", monospace',
                                lineHeight: 1.5,
                                m: 0,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                            }}
                            dangerouslySetInnerHTML={{
                                __html: syntaxHighlight(entry.formatted)
                            }}
                        />
                    </Box>
                ))}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="contained" color="secondary">Fermer</Button>
            </DialogActions>
        </Dialog>
    );
};

JsonViewerDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    title: PropTypes.node,
    jsonEntries: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.string,
        dateIntegration: PropTypes.string,
        dateModification: PropTypes.string,
    })),
};

export default JsonViewerDialog;
